import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Database, Plus, Trash2, Zap, FileSpreadsheet, Search, AlertTriangle, Star, Compass, Boxes, ChevronDown, ChevronRight, RefreshCw, ArrowLeftRight } from "lucide-react";
import { widgetDatasetIds } from "@/lib/widgetCatalog";
import { ReplaceSourceDialog } from "@/components/bi/ReplaceSourceDialog";
import { cn } from "@/lib/utils";

// Dashboard-level data sources: attach/detach datasets, see widget usage, pick active.
export function DataSourcesPanel({ dashboardId, datasets, attached, widgets, activeDatasetId, onPickActive, onSourcesChanged, onWidgetsRemoved, onSelectWidget = null, onRefreshSource = null, onSourceReplaced = null, canEdit = true }) {
  const [q, setQ] = useState("");
  const [listQ, setListQ] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [detachTarget, setDetachTarget] = useState(null); // {dataset_id, name, usage:[...]}
  const [replaceTarget, setReplaceTarget] = useState(null); // {dataset_id, name}
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState({});      // dataset_id -> {status, message}
  const [refreshing, setRefreshing] = useState({});

  const usage = useMemo(() => {
    const m = {};
    (widgets || []).forEach((w) => {
      widgetDatasetIds(w).forEach((did) => {
        (m[did] = m[did] || []).push({ id: w.id, title: w.title || w.type, type: w.type });
      });
    });
    return m;
  }, [widgets]);

  const byId = useMemo(() => Object.fromEntries(datasets.map((d) => [d.id, d])), [datasets]);
  const attachedIds = useMemo(() => attached || [], [attached]);
  const implicitIds = useMemo(
    () => Object.keys(usage).filter((did) => !attachedIds.includes(did)),
    [usage, attachedIds]);
  const unattached = datasets.filter((d) => !attachedIds.includes(d.id)
    && (!q || d.name.toLowerCase().includes(q.toLowerCase())));
  // Item 3: the panel's own search narrows the attached/used list without hiding the header.
  const lq = listQ.trim().toLowerCase();
  const nameOf = (did) => byId[did]?.name || "";
  const listMatch = (did) => !lq || nameOf(did).toLowerCase().includes(lq);
  const shownAttached = attachedIds.filter(listMatch);
  const shownImplicit = implicitIds.filter(listMatch);

  // Source health: probe each attached/used dataset once (server caches for 60s).
  const allIds = useMemo(() => [...new Set([...attachedIds, ...implicitIds])], [attachedIds, implicitIds]);
  useEffect(() => {
    allIds.forEach((did) => {
      if (status[did] || !byId[did]) return;
      api.get(`/datasets/${did}/status`).then(({ data }) => setStatus((s) => ({ ...s, [did]: data })))
        .catch(() => setStatus((s) => ({ ...s, [did]: { status: "error", message: "Status unavailable" } })));
    });
  }, [allIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async (did) => {
    setRefreshing((r) => ({ ...r, [did]: true }));
    try {
      const { data } = await api.post(`/datasets/${did}/refresh`);
      const st = data?.status || data?.probe?.status || "ok";
      setStatus((s) => ({ ...s, [did]: { ...(s[did] || {}), status: st, message: data?.message || data?.probe?.message || "" } }));
      onRefreshSource && onRefreshSource(did);
      st === "ok" ? toast.success(`${byId[did]?.name || "Source"} refreshed`) : toast.error(data?.message || "Refresh failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setRefreshing((r) => ({ ...r, [did]: false })); }
  };

  const attach = async (datasetId) => {
    try {
      const { data } = await api.post(`/dashboards/${dashboardId}/data-sources`, { dataset_id: datasetId });
      onSourcesChanged(data.data_sources);
      toast.success("Source attached to this dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to attach source"); }
  };

  const detach = async (datasetId, mode) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/dashboards/${dashboardId}/data-sources/${datasetId}`, { params: { mode } });
      onSourcesChanged(data.data_sources);
      if (mode === "remove_widgets" && data.removed_widgets) {
        onWidgetsRemoved && onWidgetsRemoved(datasetId);
        toast.success(`Source detached — ${data.removed_widgets} widget(s) removed`);
      } else toast.success("Source detached");
      setDetachTarget(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.widgets) {
        setDetachTarget({ dataset_id: datasetId, name: byId[datasetId]?.name || datasetId, usage: detail.widgets });
      } else toast.error(typeof detail === "string" ? detail : "Failed to detach source");
    } finally { setBusy(false); }
  };

  const row = (did, { implicit = false } = {}) => {
    const d = byId[did];
    const used = usage[did] || [];
    const st = !d ? { status: "missing", message: "Dataset was deleted or is no longer accessible" } : status[did];
    const dot = st?.status === "ok" ? "bg-[color:var(--brand-mint)]" : st?.status ? "bg-[color:var(--danger)]" : "bg-[color:var(--ink-3)]/40";
    return (
      <React.Fragment key={did}>
      <div data-testid="source-row"
        onClick={() => onPickActive(did)}
        className={cn("group flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-xs hover:bg-[color:var(--surface-2)]",
          activeDatasetId === did && "bg-[color:rgba(43,78,255,0.07)]")}>
        {d?.mode === "live" ? <Zap className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-mint)]" /> : <FileSpreadsheet className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-ocean)]" />}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 min-w-0">
            <span data-testid="source-status-dot" data-status={st?.status || "unknown"} title={st?.message || "Checking…"} className={cn("w-1.5 h-1.5 rounded-full shrink-0", dot)} />
            <span className="truncate text-[color:var(--ink-2)] font-medium" title={d?.name || "(missing dataset)"}>{d?.name || "(missing dataset)"}</span>
            {activeDatasetId === did && <Star className="w-2.5 h-2.5 shrink-0 text-amber-400" fill="currentColor" />}
            {st?.status && st.status !== "ok" && <span data-testid="source-error-badge" title={st.message} className="text-[9px] px-1 rounded bg-[color:rgba(220,38,38,0.1)] text-[color:var(--danger)] shrink-0">{st.status === "missing" ? "missing" : "error"}</span>}
          </div>
          <div className="text-[10px] text-[color:var(--ink-3)] flex items-center gap-1">
            {used.length ? (
              <button data-testid="source-usage-toggle" onClick={(e) => { e.stopPropagation(); setExpanded(expanded === did ? null : did); }} className="inline-flex items-center gap-0.5 hover:text-[color:var(--brand-ocean)]">
                {expanded === did ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}{used.length} widget{used.length > 1 ? "s" : ""}
              </button>
            ) : "not used yet"}{implicit ? " · not attached" : ""}
            {d && <span className="opacity-60">· {d.mode === "live" ? `live · ${d.db_type || "db"}` : `${(d.row_count || 0).toLocaleString()} rows`}</span>}
          </div>
        </div>
        <span className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100">
          {d && canEdit && (
            <button data-testid="source-refresh-btn" title="Refresh this source (bust cache, re-run its widgets)" disabled={!!refreshing[did]}
              onClick={(e) => { e.stopPropagation(); refresh(did); }}
              className="p-0.5 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] disabled:opacity-50">
              <RefreshCw className={cn("w-3.5 h-3.5", refreshing[did] && "animate-spin")} />
            </button>
          )}
          {canEdit && used.length > 0 && (
            <button data-testid="source-replace-btn" title="Replace this source with another dataset"
              onClick={(e) => { e.stopPropagation(); setReplaceTarget({ dataset_id: did, name: d?.name || did }); }}
              className="p-0.5 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">
              <ArrowLeftRight className="w-3.5 h-3.5" />
            </button>
          )}
          <a data-testid="source-explore-link" title="View data (Explore)" href={`/explore/${did}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="p-0.5 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><Compass className="w-3.5 h-3.5" /></a>
          <a data-testid="source-model-link" title="Edit model / fields" href={`/model/${did}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="p-0.5 rounded text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]"><Boxes className="w-3.5 h-3.5" /></a>
        </span>
        {implicit ? (
          <button data-testid="source-promote-btn" title="Attach to this dashboard"
            onClick={(e) => { e.stopPropagation(); attach(did); }}
            className="p-0.5 rounded opacity-0 group-hover:opacity-100 text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface)]">
            <Plus className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button data-testid="source-detach-btn" title="Detach from this dashboard"
            onClick={(e) => { e.stopPropagation(); used.length ? setDetachTarget({ dataset_id: did, name: d?.name || did, usage: used }) : detach(did, "keep"); }}
            className="p-0.5 rounded opacity-0 group-hover:opacity-100 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {expanded === did && used.length > 0 && (
        <div data-testid="source-usage-list" className="ml-7 mb-1 space-y-px">
          {used.map((u) => (
            <button key={u.id} data-testid="source-usage-widget" onClick={() => onSelectWidget && onSelectWidget(u.id)}
              className="w-full text-left text-[10px] px-2 py-0.5 rounded text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] truncate">
              {u.title} <span className="text-[color:var(--ink-3)]">· {u.type}</span>
            </button>
          ))}
        </div>
      )}
      </React.Fragment>
    );
  };

  return (
    <div className="h-full flex flex-col" data-testid="sources-panel">
      <div className="p-2 shrink-0 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-[color:var(--ink-3)] flex items-center gap-1"><Database className="w-3 h-3" />Data sources</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button data-testid="source-attach-btn" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]"><Plus className="w-3.5 h-3.5 mr-0.5" />Attach</Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2" align="start">
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
              <Input data-testid="source-attach-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search datasets…" className="h-8 pl-8 text-xs bg-[color:var(--surface)]" />
            </div>
            <ScrollArea className="h-64">
              <div className="space-y-0.5 pr-1">
                {unattached.slice(0, 80).map((d) => (
                  <button key={d.id} data-testid="source-attach-pick" onClick={() => attach(d.id)} title={d.name}
                    className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs hover:bg-[color:var(--surface-2)]">
                    {d.mode === "live" ? <Zap className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-mint)]" /> : <FileSpreadsheet className="w-3.5 h-3.5 shrink-0 text-[color:var(--brand-ocean)]" />}
                    <span className="truncate flex-1 text-[color:var(--ink-2)]">{d.name}</span>
                    <span className="text-[10px] text-[color:var(--ink-3)] shrink-0">{d.row_count?.toLocaleString?.() || ""}</span>
                  </button>
                ))}
                {unattached.length > 80 && (
                  <p data-testid="source-attach-more" className="text-[10px] text-[color:var(--ink-3)] px-2 py-1">
                    Showing 80 of {unattached.length} — keep typing to narrow the list.
                  </p>
                )}
                {!unattached.length && <p className="text-xs text-[color:var(--ink-3)] p-2 text-center">{q ? "No dataset matches that search" : "Every dataset is already attached"}</p>}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      </div>
      {(attachedIds.length + implicitIds.length) > 4 && (
        <div className="px-2 pb-2 shrink-0">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
            <Input data-testid="sources-list-search" value={listQ} onChange={(e) => setListQ(e.target.value)}
              placeholder="Find a source…" className="h-8 pl-8 text-xs bg-[color:var(--surface)]" />
          </div>
        </div>
      )}
      <ScrollArea className="flex-1 min-h-0">
        <div className="px-2 pb-4 space-y-0.5">
          {attachedIds.length === 0 && implicitIds.length === 0 && (
            <p className="text-xs text-[color:var(--ink-3)] p-3 text-center">No sources yet — attach a dataset to scope this dashboard's field pickers, or just add a widget and its dataset shows up here.</p>
          )}
          {lq && !shownAttached.length && !shownImplicit.length && (attachedIds.length + implicitIds.length) > 0 && (
            <p data-testid="sources-no-match" className="text-xs text-[color:var(--ink-3)] p-3 text-center">No source matches “{listQ}”.</p>
          )}
          {shownAttached.map((did) => row(did))}
          {shownImplicit.length > 0 && (
            <>
              <div className="text-[10px] uppercase tracking-wide text-[color:var(--ink-3)] px-1 pt-3 pb-1">Used by widgets (not attached)</div>
              {shownImplicit.map((did) => row(did, { implicit: true }))}
            </>
          )}
        </div>
      </ScrollArea>

      <ReplaceSourceDialog open={!!replaceTarget} onOpenChange={(v) => !v && setReplaceTarget(null)}
        dashboardId={dashboardId} source={replaceTarget} datasets={datasets}
        onReplaced={(data) => { setStatus((s) => { const n = { ...s }; delete n[data.new_dataset_id]; return n; }); onSourceReplaced && onSourceReplaced(data); }} />

      <Dialog open={!!detachTarget} onOpenChange={(v) => !v && setDetachTarget(null)}>
        <DialogContent className="max-w-md" data-testid="source-detach-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Detach “{detachTarget?.name}”?</DialogTitle>
            <DialogDescription>{detachTarget?.usage?.length} widget(s) on this dashboard use this source:</DialogDescription>
          </DialogHeader>
          <div className="max-h-40 overflow-auto space-y-1">
            {(detachTarget?.usage || []).map((w) => (
              <div key={w.id} className="text-xs px-2 py-1 rounded-md bg-[color:var(--surface-2)] text-[color:var(--ink-2)] truncate">{w.title || w.type} <span className="text-[color:var(--ink-3)]">· {w.type}</span></div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDetachTarget(null)}>Cancel</Button>
            <Button data-testid="source-detach-keep" variant="outline" disabled={busy}
              onClick={() => detach(detachTarget.dataset_id, "keep")}>Detach, keep widgets</Button>
            <Button data-testid="source-detach-remove" disabled={busy} className="bg-[color:var(--danger)] text-white"
              onClick={() => detach(detachTarget.dataset_id, "remove_widgets")}>Detach & remove widgets</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
